import { Component, EventEmitter, Output, HostListener, ElementRef, ViewChild, OnDestroy} from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ZXingScannerModule } from '@zxing/ngx-scanner';
import { BarcodeFormat } from '@zxing/library';
import { CommonModule } from '@angular/common';
import { HttpService } from '../../services/http.service';
import { ConstantsService } from '../../constants/constants.service';
import { DateFormatPipe } from '../../pipes/date-format.pipe';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Router } from '@angular/router';
import { NgxImageCompressService } from 'ngx-image-compress';

type ModalType =
  | 'scanner'
  | 'visitorCard'
  | 'error'
  | 'invalidQR'
  | 'uniqueQR'
  | 'pendingQR'
  | 'cancelConfirm'
  | 'captureAlert'
  | 'captureAlertOptional'
  | 'camera'
  | 'preview'
  | null;
@Component({
  selector: 'app-qr-scanner',
  standalone: true,
  imports: [CommonModule, ZXingScannerModule, DateFormatPipe],
  templateUrl: './qr-scanner.component.html',
  styleUrl: './qr-scanner.component.scss',
  animations: [
    trigger('flipCard', [
      state('default', style({
        transform: 'rotateY(0deg)'
      })),
      state('flipped', style({
        transform: 'rotateY(180deg)'
      })),
      transition('default <=> flipped', [
        animate('600ms ease-in-out')
      ])
    ])
  ]
})
export class QrScannerComponent implements OnDestroy {
  @Output() scanned = new EventEmitter<string | null>();
  @ViewChild('scannerInput', { static: false }) scannerInput!: ElementRef<HTMLInputElement>;
  @ViewChild('videoElement', { static: false }) videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasElement', { static: false }) canvasElement!: ElementRef<HTMLCanvasElement>;
  
  // Scanner mode control
  scannerMode: 'barcode' | 'camera' = 'barcode'; // Default to barcode scanner

  
  // Common properties
  scannerEnabled = true;
  // scanCompleted = false;
  scannedResult: any = {};
  visitDetails: any = {};
  frontSide: boolean = true;
  backSide: boolean = false;
  cardState: 'default' | 'flipped' = 'default';
  // isError = false;
  // invalidQRError = false;
  errorMessage = '';
  // showCancleModal: boolean = false;
  visitsId: any;
  checkInLoader: boolean = false;
  checkOutLoader: boolean = false;
  hostDetails: any = {};
  showAssets = false;
  showPurposeDetails = false;
  imageLoading: boolean = false;
  // uniqueQRresult: boolean = false;
  // pendingQRresult: boolean = false;
  
  // Capture at gate properties
  // isCameraOpen: boolean = false;
  stream: MediaStream | null = null;
  tempPreview: string | null = null;
  facingMode: string = 'user';
  videoConstraints: MediaStreamConstraints = {
    video: { facingMode: 'user' },
  };
  // showCaptureAlertModal: boolean = false;
  // showCapturePreviewModal: boolean = false;
  captureLoader: boolean = false;
  gatePhotoFile: File | null = null;

  
  // Camera scanner properties
  currentDevice: MediaDeviceInfo | undefined;
  formatsEnabled: BarcodeFormat[] = [BarcodeFormat.QR_CODE];
  cameraSupported = false;
  
  // Barcode scanner properties
  private scanBuffer = '';
  private scanTimeout: any;
  private readonly SCAN_TIMEOUT = 100; // ms between characters for scanner input
  isMobileDevice: boolean = false;
  
  // NEW: Track if modal is ready and ignore initial input
  private isModalReady = false;
  private ignoreInputUntil = 0;
  visitorPhotoMode: string = 'MANDATORY';
  activeModal: ModalType = 'scanner';
  modalStack: ModalType[] = [];

  constructor(
    private toastr: ToastrService, 
    private httpService: HttpService,
    private constants: ConstantsService,
    private router: Router,
    private imageCompress: NgxImageCompressService
  ) { }


  ngOnInit() {
    this.checkCameraSupport();
    
    // Detect if mobile device
    this.isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
    
    const storage = this.constants.getSession();
    if (storage) {
      this.hostDetails = storage;
    }
    
    // Clear any buffered input immediately
    this.scanBuffer = '';
    
    // Ignore any input for the first 500ms to prevent auto-fill issues
    this.ignoreInputUntil = Date.now() + 500;
    
    // Mark modal as ready after a short delay to ensure DOM is ready
    setTimeout(() => {
      this.isModalReady = true;
      // Clear the input field when modal becomes ready
      if (this.scannerInput?.nativeElement) {
        this.scannerInput.nativeElement.value = '';
      }
      // Only focus on scanner input if NOT a mobile device
      if (this.scannerMode === 'barcode' && !this.isMobileDevice) {
        this.focusOnScannerInput();
      }
    }, 200);

    // this.fetchPhotoModeSetting();
  }
  ngOnDestroy(): void {
  this.stopCameraStream();
  if (this.scanTimeout) {
    clearTimeout(this.scanTimeout);
  }
}
  ngAfterViewInit() {
    // Clear any existing value in the scanner input
    if (this.scannerInput?.nativeElement) {
      this.scannerInput.nativeElement.value = '';
    }
    
    // Only auto-focus on desktop devices after modal is ready
    if (this.scannerMode === 'barcode' && !this.isMobileDevice && this.isModalReady) {
      setTimeout(() => {
        this.focusOnScannerInput();
      }, 100);
    }
  }

  // Toggle between camera and barcode scanner
  toggleScannerMode() {
    this.scannerMode = this.scannerMode === 'barcode' ? 'camera' : 'barcode';
    
    // Clear any buffered data
    this.scanBuffer = '';
    if (this.scanTimeout) {
      clearTimeout(this.scanTimeout);
    }
    
    if (this.scannerMode === 'camera') {
      this.startCameraScanner();
    } else if (!this.isMobileDevice) {
      // Clear input field before focusing
      if (this.scannerInput?.nativeElement) {
        this.scannerInput.nativeElement.value = '';
      }
      this.focusOnScannerInput();
    }
  }

  // Check if camera is supported
  checkCameraSupport() {
    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      navigator.mediaDevices.enumerateDevices().then(devices => {
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        this.cameraSupported = videoDevices.length > 0;
      }).catch(() => {
        this.cameraSupported = false;
      });
    } else {
      this.cameraSupported = false;
    }
  }

  // Start camera scanner
  startCameraScanner() {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    this.toastr.error('Camera is not supported on this device or browser.');
    this.switchToBarcodeScanner();
    return;
  }

  // Actually request camera permission — this is what triggers the browser prompt
  // and throws a proper error if blocked
  navigator.mediaDevices.getUserMedia({ video: true })
    .then((stream) => {
      // Permission granted — stop this test stream immediately
      stream.getTracks().forEach(track => track.stop());

      // Now enumerate to get the actual device
      return navigator.mediaDevices.enumerateDevices();
    })
    .then((devices) => {
      const videoDevices = devices.filter(d => d.kind === 'videoinput');
      if (videoDevices.length > 0) {
        this.currentDevice = videoDevices[0];
      } else {
        this.toastr.error('No camera device found on this device.');
        this.switchToBarcodeScanner();
      }
    })
    .catch((err: DOMException) => {
      console.error('Camera access error:', err);

      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        this.toastr.error(
          'Camera access is blocked. Please allow camera permission in your browser settings.',
          '',
          { timeOut: 5000 }
        );
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        this.toastr.error('No camera found on this device.');
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        this.toastr.error('Camera is already in use by another application.');
      } else {
        this.toastr.error('Unable to access camera. Please check your device settings.');
      }

      this.switchToBarcodeScanner();
    });
}

  // Switch to barcode scanner if camera fails
  switchToBarcodeScanner() {
    this.scannerMode = 'barcode';
    this.focusOnScannerInput();
  }

  // Focus on barcode scanner input
  focusOnScannerInput() {
    // Don't focus on mobile devices to prevent keyboard
    if (this.isMobileDevice || !this.isModalReady) {
      return;
    }
    
    if (this.scannerInput && this.scannerMode === 'barcode') {
      // Clear the input before focusing
      this.scannerInput.nativeElement.value = '';
      this.scanBuffer = '';
      this.scannerInput.nativeElement.focus();
    }
  }

  // Barcode scanner input method
  onScannerInput(event: Event) {
    // Ignore input during the initial delay period
    if (Date.now() < this.ignoreInputUntil) {
      const input = event.target as HTMLInputElement;
      input.value = '';
      return;
    }
    
    // CRITICAL: Only process if modal is ready and scanner is enabled
    if (this.scannerMode !== 'barcode' || !this.isModalReady || !this.scannerEnabled) {
      return;
    }
    
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();
    
    if (value) {
      this.handleQrCodeResult(value);
      input.value = ''; // Clear the input
    }
  }

  // Barcode scanner keydown events
  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    // Ignore input during the initial delay period
    if (Date.now() < this.ignoreInputUntil) {
      this.scanBuffer = '';
      return;
    }
    
    // CRITICAL: Only process if modal is ready, scanner enabled, and in barcode mode
    if (
  this.scannerMode !== 'barcode' ||
  !this.isModalReady ||
  !this.scannerEnabled ||
  this.activeModal !== 'scanner'
) {
  return;
}

    // Ignore if typing in an input field (except our hidden scanner input)
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' && target.id !== 'scannerInput') {
      return;
    }
    
    // Also ignore if typing in textareas or content editable
    if (target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }

    // Clear timeout if it exists
    if (this.scanTimeout) {
      clearTimeout(this.scanTimeout);
    }

    // Handle Enter key (scanner usually sends Enter after scanning)
    if (event.key === 'Enter') {
      event.preventDefault();
      if (this.scanBuffer.trim()) {
        this.handleQrCodeResult(this.scanBuffer.trim());
        this.scanBuffer = '';
      }
      return;
    }

    // Handle regular characters
    if (event.key.length === 1) {
      event.preventDefault();
      this.scanBuffer += event.key;
      
      // Set timeout to clear buffer if no more input comes
      this.scanTimeout = setTimeout(() => {
        if (this.scanBuffer.trim()) {
          this.handleQrCodeResult(this.scanBuffer.trim());
        }
        this.scanBuffer = '';
      }, this.SCAN_TIMEOUT);
    }
  }

  // Barcode scanner clipboard events
  @HostListener('document:paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    if (this.scannerMode !== 'barcode' || 
        !this.isModalReady || 
        !this.scannerEnabled || 
        this.activeModal !== 'scanner') {
      return;
    }

    const clipboardData = event.clipboardData?.getData('text');
    if (clipboardData) {
      event.preventDefault();
      this.handleQrCodeResult(clipboardData.trim());
    }
  }

  // Handle QR code result from both methods
  handleQrCodeResult(result: string) {
    try {
      console.log('Scanner Input:', result);
      
      // Check for your special Unique QR code first
      if (result === 'UniqueVBQr') {
        this.openModal('uniqueQR');
        console.log('Unique QR detected!');
        return;
}

      //Pending QR Code check
      if (result === 'PendingVBQr') {
        this.openModal('pendingQR');
        console.log('Pending QR detected!');
        return;
      }

      // Try to parse as JSON first
      let parsedResult;
      try {
        parsedResult = JSON.parse(result);
      } catch {
        // If not JSON, treat as plain text and try to extract visitId
        if (result.includes('visitId')) {
          // Handle case where scanner might read: visitId="1703164d-ae6d-4251-9033-4e5267405de0"
          const visitIdMatch = result.match(/visitId["\s]*[:=]["\s]*([a-f0-9-]+)/i);
          if (visitIdMatch) {
            parsedResult = { visitId: visitIdMatch[1] };
          }
        } else if (this.isValidUUID(result)) {
          // If the result is a valid UUID, assume it's the visitId
          parsedResult = { visitId: result };
        } else {
          throw new Error('Invalid QR Code format');
        }
      }

      this.scannedResult = parsedResult;
      console.log('Parsed QR Code Result:', parsedResult);
      
      if (parsedResult?.visitId) {
        this.openModal('visitorCard');
        this.imageLoading = true;
        this.getVisitDetail(parsedResult.visitId);
      } else {
        throw new Error('Invalid QR Code format - missing visitId');
      }

    } catch (error) {
      console.error('QR Code parsing error:', error);
      this.openModal('invalidQR');
      this.toastr.error('Invalid QR Code format. Please try again.');
    }
  }

  // Helper method to validate UUID format
  private isValidUUID(str: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  }

  closeScanner() {
  this.stopCameraStream();
  this.activeModal = null;

  this.modalStack = [];

  this.scannerEnabled = false;
  this.currentDevice = undefined;

  this.scanBuffer = '';

  if (this.scanTimeout) {
    clearTimeout(this.scanTimeout);
  }

  this.scanned.emit(null);
}

  getVisitDetail(visitId: any) {
    this.httpService.getRequest(this.constants.SERVER_URLS['VISIT_DETAILS_QR'] + visitId)
      .subscribe({
        next: (res: any) => {
          if (res.status === this.constants.RESPONSE_CODES['SUCCESS']) {
            this.visitDetails = res.result;
            this.openModal('visitorCard');
            setTimeout(() => {
              this.imageLoading = false;
            }, 300);
          } else {
            this.openModal('error');
            this.errorMessage = res.message;
            this.imageLoading = false;
          }
        },
        error: (err) => {
          this.openModal('error');
          this.errorMessage = err?.error?.message || 'Something went wrong';
          this.imageLoading = false;
        },
      });
  }

  closeVisitorCard() {
  this.frontSide = true;
  this.backSide = false;
  this.showAssets = false;
  this.showPurposeDetails = false;
  this.cardState = 'default';
  this.imageLoading = false;

  this.openModal('scanner');

  if (this.scannerMode === 'barcode' && !this.isMobileDevice) {
    setTimeout(() => {
      this.focusOnScannerInput();
    }, 100);
  }
}

  getVisitTypeClass(visitType: string): string {
    switch (visitType) {
      case 'VIP':
        return 'vip-tag';
      case 'Interview':
        return 'interview-tag';
      case 'Business':
        return 'business-tag';
      case 'Personal':
        return 'personal-tag';
      default:
        return 'default-tag';
    }
  }

  flipCard(): void {
    if (!this.imageLoading) {
      this.frontSide = !this.frontSide;
      this.backSide = !this.backSide;
      this.cardState = this.frontSide ? 'default' : 'flipped';
    }
  }

  checkedInVisit(visitDetails: any): void {
  if (visitDetails.visitor?.isBlacklist) {
    this.toastr.warning('Cannot check in because the visitor is blacklisted.');
    return;
  }

  if (visitDetails.captureAtGate === true) {
    this.openModal('captureAlert', true);
    return;
  }

  if (!visitDetails.photo && visitDetails.visitorPhotoMode === 'OPTIONAL') {
    this.openModal('captureAlertOptional', true);
    return;
  }

  this.performCheckIn(visitDetails);
}

  performCheckIn(visitDetails: any): void {
    this.checkInLoader = true;
    console.log('Checked In Visit ID:', visitDetails.visitId);

    this.httpService.putRequest(this.constants.SERVER_URLS['CHECK_IN_VISITS'] + visitDetails.visitId +
          '?mode=OVERRIDE', {})
      .subscribe(
        (res: any) => {
          this.checkInLoader = false;
          if (res.status === this.constants.RESPONSE_CODES['SUCCESS']) {
            this.toastr.success(res.message || 'Checked-in successfully');
          }
          this.closeScanner();
        },
        (error) => {
          this.checkInLoader = false;
          const msg = error.error?.message || 'Check-in failed';
          this.toastr.error(msg, 'Error');
        }
      );
  }

  // Capture at gate alert modal handlers
  handleCaptureAlertClose(): void {
  this.restorePreviousModal();
}

  handleCaptureAlertCapture(): void {
  this.openCamera();
}

  // Camera capture methods (adapted from create-visit component)
  openCamera(): void {
    this.openModal('camera', true);
    this.facingMode = 'user';

    setTimeout(() => {
      const constraints: MediaStreamConstraints = {
        video: { facingMode: this.facingMode },
        audio: false,
      };

      navigator.mediaDevices
        .getUserMedia(constraints)
        .then((stream) => {
          this.stream = stream;
          const video = this.videoElement?.nativeElement;

          if (video) {
            video.srcObject = stream;
            video.autoplay = true;
            video.muted = true;
            video.playsInline = true;
            video.removeAttribute('controls');
            video.setAttribute('playsinline', 'true');
            video.setAttribute('muted', 'true');
            video.setAttribute('autoplay', 'true');
            video.setAttribute('disablepictureinpicture', 'true');
          }
        })
        .catch((err) => {
          console.error('Error accessing camera:', err);
          this.restorePreviousModal();
          this.toastr.error('Could not access camera. Please allow camera permissions.');
        });
    }, 0);
  }

  capturePhoto(): void {
    const video = this.videoElement?.nativeElement;
    if (!video || !this.canvasElement) return;

    const canvas = this.canvasElement.nativeElement;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      this.toastr.error('Could not initialize canvas context');
      return;
    }
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const capturedImage = canvas.toDataURL('image/jpeg');
    this.stopCameraStream();
    if (typeof capturedImage === 'string') {
      const file = this.dataURLtoFile(capturedImage, 'gate-capture.jpg');
      const originalSizeMB = (file.size / (1024 * 1024)).toFixed(2);

      if (file.size > 5 * 1024 * 1024) {
        // Compress if larger than 5MB
        this.imageCompress
          .compressFile(capturedImage, -1, 60, 60)
          .then((compressedBase64) => {
            this.base64ToFile(compressedBase64, file.name).then(
              (compressedFile) => {
                const compressedSizeMB = (
                  compressedFile.size /
                  (1024 * 1024)
                ).toFixed(2);

                console.log(`Captured image size: ${originalSizeMB} MB`);
                console.log(`Compressed image size: ${compressedSizeMB} MB`);
                this.toastr.info(
                  `Compressed from ${originalSizeMB}MB to ${compressedSizeMB}MB`
                );

                this.assignCapturedFile(compressedFile, compressedBase64);
              }
            );
          });
      } else {
        // No compression needed
        console.log(
          `Captured image size (not compressed): ${originalSizeMB} MB`
        );
        this.assignCapturedFile(file, capturedImage);
      }
    }

    // this.closeCamera();
  }

  assignCapturedFile(file: File, previewBase64: string): void {
    this.gatePhotoFile = file;
    this.tempPreview = previewBase64;
    this.openModal('preview', true);
  }

  closeCamera(): void {
  this.stopCameraStream();
  this.restorePreviousModal();
}

  switchCamera(): void {
  // Toggle between front and back camera
  this.facingMode = this.facingMode === 'user' ? 'environment' : 'user';

  // Stop current stream
  this.stopCameraStream();

  // Reopen camera with new facing mode
  if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
    navigator.mediaDevices
      .getUserMedia({
        video: { facingMode: this.facingMode },
      })
      .then((stream) => {
        this.stream = stream;
        if (this.videoElement?.nativeElement) {
          this.videoElement.nativeElement.srcObject = stream;
        }
      })
      .catch((error) => {
        console.error('Error switching camera:', error);
        this.toastr.error('Could not switch camera.');
      });
  }
  }

  dataURLtoFile(dataurl: string, filename: string): File {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  }

  base64ToFile(base64: string, filename: string): Promise<File> {
    return fetch(base64)
      .then((res) => res.blob())
      .then((blob) => new File([blob], filename, { type: blob.type }));
  }

  // Preview modal handlers
  handlePreviewChange(): void {
  this.restorePreviousModal();
  // this.reopenCamera();
}

  handlePreviewSave(): void {
  if (!this.gatePhotoFile) {
    this.toastr.warning('No photo captured. Please try again.');
    return;
  }

  const savedPreview = this.tempPreview;
  this.captureLoader = true;

  this.httpService.multipartRequest(
    this.constants.SERVER_URLS['UPDATE_GATE_PHOTO'] + this.visitDetails.visitId + '?isDefault=false',
    { photo: this.gatePhotoFile },
    '',
    null
  ).subscribe({
    next: (res: any) => {
      this.captureLoader = false;

      if (res.status === this.constants.RESPONSE_CODES['SUCCESS']) {
        if (res.result?.photoUrl) {
          this.visitDetails.photoUrl = res.result.photoUrl;
        } else {
          this.visitDetails.photoUrl = savedPreview as string;
        }

        this.visitDetails.captureAtGate = false;

        this.tempPreview = null;
        this.gatePhotoFile = null;

        this.stopCameraStream();
        this.toastr.success('Photo captured successfully');

        this.activeModal = null;

        this.performCheckIn(this.visitDetails);

      } else {
        this.toastr.error(res.message || 'Failed to update photo');
      }
    },
    error: (err) => {
      this.captureLoader = false;
      this.toastr.error(err.error?.message || 'Failed to update photo', 'Error');
    }
  });
}


  checkedOutVisit(visitDetails: any): void {
    if (visitDetails.visitor?.isBlacklist) {
      this.toastr.warning('Cannot check out because the visitor is blacklisted.', 'Warning');
      return;
    }

    this.checkOutLoader = true;
    console.log('Checked Out Visit ID:', visitDetails.visitId);

    this.httpService.putRequest(this.constants.SERVER_URLS['CHECK_OUT_VISITS'] + visitDetails.visitId + '?mode=QR', {})
      .subscribe(
        (res: any) => {
          this.checkOutLoader = false;
          if (res.status === this.constants.RESPONSE_CODES['SUCCESS']) {
            this.toastr.success(res.message || 'Checked-out successfully');
          }
          this.closeScanner();
        },
        (error) => {
          this.checkOutLoader = false;
          const msg = error.error?.message || 'Check-out failed';
          this.toastr.error(msg, 'Error');
        }
      );
  }

  accessDeniedVisit(visitsId: any): void {
    this.closeCancleModal();
    console.log('Access Denied Visit ID:', visitsId);
    this.httpService.putRequest(this.constants.SERVER_URLS['ACCESS_DENIED_VISITS'] + visitsId, {}).subscribe(
      (res: any) => {
        if (res.status === this.constants.RESPONSE_CODES['SUCCESS']) {
            this.toastr.success(res.message || 'Visit denied successfully');
          }
        this.closeScanner();
      },
      (error) => {
        const msg = error.error?.message || 'Access denied failed'
        this.toastr.error(msg, 'Error');
      }
    );
  }

  openCancelModal() {
    this.openModal('cancelConfirm', true);
    this.scannerEnabled = false; 
  }

  closeCancleModal() {
  this.scannerEnabled = true;
  this.restorePreviousModal();
}

  onCancel(visitsId: any): void {
    console.log('Cancel Visit ID:', visitsId);
    this.httpService.putRequest(this.constants.SERVER_URLS['CANCEL_VISITS'] + visitsId, {}).subscribe((res: any) => {
      if (res.status === this.constants.RESPONSE_CODES['SUCCESS']) {
        this.toastr.success(res.message, 'Success');
        this.closeCancleModal();
        this.closeScanner();
      } else {
        this.toastr.error(res.message, 'Error');
      }
    },
    (error) => {
      this.toastr.error('Failed to cancel the visit', 'Error');
    });
  }

  getStatusIcon(status: string): { icon: string; tooltip: string } {
    switch (status) {
      case 'Pending_Approvals':
      case 'Pending_Manager_Approvals':
      case 'Pending_Host_Review':
       return {
        icon: 'assets/icons/pendingIcon.svg',
        tooltip: 'Pending Approval',
       };
      case 'Checked_Out':
        return {
          icon: 'assets/icons/completed-icon.svg',
          tooltip: 'Checked_Out',
        };
      case 'Scheduled':
        return { icon: 'assets/icons/upcoming-icon.svg', tooltip: 'Scheduled' };
      case 'Checked_In':
        return { icon: 'assets/icons/checked-in.svg', tooltip: 'Checked In' };
      case 'Cancelled':
        return { icon: 'assets/icons/canceled-icon.svg', tooltip: 'Cancelled' };
      default:
        return {
          icon: 'assets/icons/default-icon.png',
          tooltip: 'Unknown Status',
        };
    }
  }

  fetchPhotoModeSetting(): void {
  this.httpService
    .getRequest(this.constants.SERVER_URLS['SETTING'] + '/declaration-status')
    .subscribe((res: any) => {
      if (res.status === this.constants.RESPONSE_CODES['SUCCESS']) {
        this.visitorPhotoMode = res.result.visitorPhotoMode || 'MANDATORY';
      }
    });
}
handleCaptureAlertSkip(): void {
  this.restorePreviousModal();

  // Fetch the default avatar image and upload it as the gate photo
  fetch('assets/images/User img.png')
    .then(res => res.blob())
    .then(blob => {
      const defaultFile = new File([blob], 'default-avatar.svg', { type: blob.type });

      this.httpService.multipartRequest(
        this.constants.SERVER_URLS['UPDATE_GATE_PHOTO'] + this.visitDetails.visitId + '?isDefault=true',
        { photo: defaultFile },
        '',
        null
      ).subscribe({
        next: (res: any) => {
          if (res.status === this.constants.RESPONSE_CODES['SUCCESS']) {
            // Update local photo so card shows default image instead of gate placeholder
            if (res.result?.photoUrl) {
              this.visitDetails.photoUrl = res.result.photoUrl;
            }
            this.visitDetails.captureAtGate = false;
          }
          // Proceed to check-in regardless
          this.performCheckIn(this.visitDetails);
        },
        error: () => {
          // Even if upload fails, still check in (OPTIONAL = permissive)
          this.performCheckIn(this.visitDetails);
        }
      });
    })
    .catch(() => {
      // If fetch of default image fails, just check in directly
      this.performCheckIn(this.visitDetails);
    });
}
openModal(modal: ModalType, saveCurrent = false) {
  if (saveCurrent && this.activeModal) {
    this.modalStack.push(this.activeModal);
  }

  this.activeModal = modal;
}

restorePreviousModal() {
  this.activeModal = this.modalStack.pop() || 'scanner';
  if (this.activeModal === 'camera') {
    this.reopenCamera();
  }
}

closeCurrentModal() {
  this.activeModal = null;
}
reopenCamera(): void {
  setTimeout(() => {
    const constraints: MediaStreamConstraints = {
      video: { facingMode: this.facingMode },
      audio: false,
    };

    navigator.mediaDevices
      .getUserMedia(constraints)
      .then((stream) => {
        this.stream = stream;
        const video = this.videoElement?.nativeElement;
        if (video) {
          video.srcObject = stream;
          video.autoplay = true;
          video.muted = true;
          video.playsInline = true;
          video.play();
        }
      })
      .catch((err) => {
        console.error('Error restarting camera:', err);
        this.toastr.error('Could not restart camera.');
        this.restorePreviousModal(); // go back to captureAlert
      });
  }, 100);
}
stopCameraStream(): void {
  if (this.stream) {
    this.stream.getTracks().forEach((track) => track.stop());
    this.stream = null;
  }
}
}
